import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Button, Typography } from 'antd';
import ReactPlayer from 'react-player';
import { FaPlay } from 'react-icons/fa';
import lpOne from 'src/assets/images/thumb-2.png';
import apiRoutes from 'src/utils/apiRoutes';
import apiRequests from 'src/utils/api';
import { useDispatch, useSelector } from 'react-redux';
import { UserResponse } from 'src/types';
import { updateAuthData } from 'src/store/auth/reducer';
import { useIsMobile } from '.';
import { LOCAL_STORAGE_CURRENT_CUSTOMER_KEY } from 'src/utils/constants';
import { getCurrentCustomer } from 'src/utils/localStorage';
import { setOnboardingStep } from 'src/store/projectRetainer/reducer';
import asyncErrorHandler from 'src/utils/asyncErrorHandler';

const { Text, Link } = Typography;

interface StepFiveProps {}

const StepFive: FC<StepFiveProps> = () => {
  const isMobile = useIsMobile();

  const [playing, setPlaying] = useState(false);
  const dispatch = useDispatch();

  const user: UserResponse = useSelector(
    (globalState: any) => globalState.auth.user
  );

  const currentCustomer = getCurrentCustomer();

  // getCurrentCustomer() returns a new object on every render, so the effect below re-runs
  // each time; this keeps the completion request to a single call.
  const completionSent = useRef(false);

  const completeOnboarding = useCallback(
    async (customerUuid: string) => {
      try {
        await apiRequests.put(`${apiRoutes.CUSTOMERS}/${customerUuid}`, {
          onboarding_step: 1,
        });
      } catch (error) {
        asyncErrorHandler(error);
        return;
      }

      dispatch(
        updateAuthData({
          contact: {
            ...user.contact,
            customer: {
              ...currentCustomer,
              onboarding_step: 1,
            },
          },
        })
      );

      dispatch(setOnboardingStep(1));

      localStorage.setItem(
        LOCAL_STORAGE_CURRENT_CUSTOMER_KEY,
        JSON.stringify({
          ...currentCustomer,
          onboarding_step: 1,
        })
      );
    },
    [dispatch, currentCustomer, user.contact]
  );

  useEffect(() => {
    if (user && currentCustomer && !completionSent.current) {
      completionSent.current = true;
      completeOnboarding(currentCustomer.uuid);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, currentCustomer]);

  return (
    <div className="w-full space-y-6">
      <div className="text-center">
        <div className="text-2xl font-bold text-primary">You're all set</div>

        <div className="text-gray-500">
          Now the fun begins. Submit your first request and let’s make something
          epic.
        </div>
      </div>

      <div
        className={`overflow-hidden rounded-lg ${
          playing ? '' : 'pointer-events-none absolute opacity-0'
        } ${isMobile ? '' : 'h-[373px] w-[672px]'}`}
      >
        <ReactPlayer
          url="https://vimeo.com/1102041091"
          width="100%"
          height="100%"
          playing={playing}
          style={{
            overflow: 'hidden',
            borderRadius: '8px',
            aspectRatio: 1.8,
          }}
          controls
        />
      </div>

      {!playing && (
        <button
          className="relative block w-full"
          type="button"
          onClick={() => setPlaying(true)}
        >
          <img
            src={lpOne}
            alt="thumbnail"
            className={`w-full rounded-lg  ${
              isMobile ? '' : 'h-[373px] w-[666px]'
            }`}
            style={{ aspectRatio: 1.8 }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="rounded-full bg-blue-100 p-3">
              <FaPlay className="ml-0.5" />
            </div>
          </div>
        </button>
      )}

      <div className="text-center">
        <Link href="/requests/create">
          <Button type="primary">Submit request now</Button>
        </Link>
      </div>

      <div className="w-full text-center" style={{ marginTop: '2rem' }}>
        <Text type="secondary">
          Still have questions or need direction?{' '}
          <Link
            href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ3QB3AH6chN95CPTulUgRc75Ep7xwdAQBSrl_DuXjilEvTqliuaTTIGxiHfWc-0yOHur5EZ6HYc"
            target="_blank"
            className="underline-hover-link"
          >
            Schedule a kickoff call.
          </Link>
        </Text>
      </div>
    </div>
  );
};

export default StepFive;
